<template>
  <li class="dropdown notification-list">
    <a
      class="nav-link dropdown-toggle arrow-none text-decoration-none"
      href="#"
      @click.prevent="toggleNotificationDropDown"
    >
      <i class="mdi mdi-bell-outline noti-icon font-24"></i>
      <span class="noti-icon-text-badge" v-show="unseen_notification_count > 0">
        <template v-if="unseen_notification_count > 99">99+</template>
        <template v-else>{{ unseen_notification_count }}</template>
      </span>
    </a>
    <dropdown item-class="dropdown-lg" :show="showContainer">
      <!-- item-->
      <div class="dropdown-item noti-title">
        <h5 class="m-0">
          <span class="float-end" v-show="panelNotifications.length">
            <router-link :to="{ name: 'retailer.notifications' }">
              <small>{{ $t('views.layouts.display_all') }}</small>
            </router-link></span
          >{{ $t('views.layouts.notification') }}
        </h5>
      </div>

      <div class="slimScrollDiv" @click="notificationClick">
        <!-- item-->
        <template v-if="panelNotifications.length">
          <div class="slimscroll">
            <span
              class="dropdown-item notify-item"
              :class="{ 'cursor-pointer': isRedirectUrl === true }"
              v-for="notification in panelNotifications"
              :key="notification.id"
            >
              <div class="notify-icon bg-primary">
                <i class="mdi" :class="getIcon(notification.type)"></i>
              </div>
              <p class="notify-details clarity-mask">
                <span v-html="notification.data.message"></span>
                <small class="text-muted">
                  {{ $filters.humanReadable(notification.created_at) }}
                </small>
              </p>
            </span>
          </div>
        </template>

        <div v-else class="dropdown-item notify-item">
          <p>{{ $t('views.layouts.no_new_notification') }}</p>
        </div>
      </div>
    </dropdown>
  </li>
</template>

<script>
import { storeToRefs } from 'pinia'
import _ from 'lodash'
import { event } from 'vue-gtag'
import { useNotificationStore } from '@/stores/helpers/notification.js'
import { useAuthStore } from '@/stores/retailer/auth.js'
import { useUserStore } from '@/stores/retailer/user.js'
import { NOTIFICATION_SEVERITY_TYPES, NOTIFICATION_TYPES } from '@/constants/constants.js'
import Dropdown from '@/components/Dropdown.vue'

const notificationDefaults = {
  severity: NOTIFICATION_SEVERITY_TYPES.INFO,
  is_dismissible: true,
  type: NOTIFICATION_TYPES.TOAST
}

export default {
  components: {
    Dropdown
  },
  setup() {
    const authStore = useAuthStore()
    const userStore = useUserStore()
    const notificationStore = useNotificationStore()
    const { unread_notifications, user, show_notification_container, unseen_notification_count } =
      storeToRefs(authStore)

    return {
      unread_notifications,
      user,
      show_notification_container,
      unseen_notification_count,
      authStore,
      userStore,
      notificationStore
    }
  },
  data() {
    return {
      showNotificationContainer: false,
      unreadNotifications: null,
      isRedirectUrl: false
    }
  },
  beforeUnmount() {
    window.removeEventListener('click', this.close)
  },
  methods: {
    getNotifications() {
      return this.authStore.getNotifications()
    },
    markAsReadNotification(id) {
      return this.authStore.markAsReadNotification(id)
    },
    markNotifications() {
      this.userStore.markNotificationsAsRead(this.user.id).then(response => {
        this.unreadNotifications = response.data
      })
    },
    toggleNotificationDropDown() {
      this.authStore.setShowNotificationContainer(!this.show_notification_container)

      if (this.unseen_notification_count > 0) {
        this.authStore.markAsSeenNotifications()
      }

      if (this.show_notification_container) {
        event('page_actions', {
          event_action: 'notification_opened'
        })
      }
    },
    getFilename(name) {
      return import.meta.env.VITE_BACKEND_URL + name
    },
    getNotificationType(type) {
      return type ? type.split('\\').pop() : ''
    },
    getIcon(type) {
      const notificationType = this.getNotificationType(type)
      if (notificationType === 'ExportReady') {
        return 'mdi-paperclip'
      } else {
        return 'mdi-bell'
      }
    },
    close(e) {
      if (!this.$el.contains(e.target)) {
        this.authStore.setShowNotificationContainer(false)
      }
    },
    notificationClick() {
      event('page_actions', {
        event_action: 'notification_clicked'
      })
    },
    getNotificationUrl(type, url) {
      const notificationType = this.getNotificationType(type)
      if (notificationType !== 'ExportReady' && url?.length > 0) {
        this.$router.push(url)
        this.isRedirectUrl = true
      }
      this.isRedirectUrl = false
    }
  },
  created() {
    this.getNotifications().then(response => {
      _.each(response, item => {
        const notificationContent = {
          ...notificationDefaults,
          ...item.data,
          id: item.id,
          created_at: item.created_at,
          is_system_notification: true
        }
        if (typeof item.data?.kind !== 'undefined') {
          this.notificationStore.add(notificationContent)
        }
      })
    })

    window.addEventListener('click', this.close)
  },
  mounted() {
    this.eventEmitter.off('notificationRead')
    this.eventEmitter.on('notificationRead', async notification => {
      if (!notification.preventReadEvent) {
        await this.markAsReadNotification(notification.id)
        if (notification.modal?.onCloseEvent) {
          notification.modal.onCloseEvent()
        }
        if (notification.kind === NOTIFICATION_TYPES.REFRESH) {
          window.location.reload()
        }
      }
    })
  },
  computed: {
    showContainer() {
      return this.show_notification_container ? 'show' : ''
    },
    panelNotifications() {
      return this.unread_notifications.filter(item => typeof item.data.kind === 'undefined')
    }
  },
  watch: {
    showNotificationContainer(value) {
      if (value === true) {
        this.markNotifications()
      } else {
        this.authStore.setUserNotifications(this.unreadNotifications)
      }
    }
  }
}
</script>

<style scoped>
.slimScrollDiv {
  height: auto;
  max-height: 300px;
  overflow-x: hidden;
}
</style>
